import { motion } from 'framer-motion';
import React, { useMemo } from 'react';
import ListGroup from 'react-bootstrap/ListGroup';
import InfiniteScroll from 'react-infinite-scroller';
import { v4 as uuidv4 } from 'uuid';

import OrdersListLoader from '../Loaders/ordersListLoader';

interface Props<T> {
  data?: T[];
  children: (item: T, index: number) => JSX.Element;
  isInitialLoading?: boolean;
  loadMore?: (pageIndex: number) => any;
  hasMore?: boolean;
  ListEmptyComponent?: React.ComponentType | React.ReactNode;
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.2 },
  },
};

const InfiniteList = <T,>({
  data = [],
  isInitialLoading = false,
  children,
  loadMore = () => {},
  hasMore = false,
  ListEmptyComponent,
}: Props<T>): JSX.Element | null => {
  const List = useMemo(() => {
    if (isInitialLoading) {
      return [...Array(5)].map(() => <OrdersListLoader key={uuidv4()} />);
    }
    if (!data || data.length === 0) {
      // Check if list empty component is a function or a ReactNode and render accordingly
      if (typeof ListEmptyComponent === 'function') {
        const EmptyComponent = ListEmptyComponent as React.ComponentType;
        return <EmptyComponent />;
      }
      return ListEmptyComponent as JSX.Element;
    }

    return (
      <div>
        <InfiniteScroll
          threshold={415}
          loadMore={loadMore}
          hasMore={hasMore}
          loader={
            <div key={0}>
              {[...Array(5)].map(() => (
                <OrdersListLoader key={`loader-${uuidv4()}`} />
              ))}
            </div>
          }
          useWindow={false}
        >
          <ListGroup variant="flush" as={motion.ul} variants={container} initial="hidden" animate="show" className="padding-start-0">
            {data.map((item, index) => children(item, index))}
          </ListGroup>
        </InfiniteScroll>
      </div>
    );
  }, [data, isInitialLoading, hasMore, ListEmptyComponent]);

  return List as JSX.Element | null;
};

export default InfiniteList;
